import { toast } from '@Backlot-Cars/archie';
import { createSlice } from '@reduxjs/toolkit';
import { BID_SALE_AUCTION_STATUS } from 'constants/bidSale';
import { MARKETPLACE_TAB, VDP_SEARCH_LIST_KEY } from 'constants/marketplace';
import { HYDRATE } from 'next-redux-wrapper';
import searchInitialState, { initSearchList, initialOfferState } from 'reducers/search/initialState';
import {
	setOvertimeCompleted as setOvertimeCompletedHelper,
	updateOfferStatus,
} from 'store/helpers/vehicleOfferUpdates';
import {
	calculatePages,
	calculatePaginationAfterAddition,
	insertVehicleIntoIdslist,
	removeFromVehicleIds,
} from 'store/helpers/watchlist';
import { closeCampaign, loadFeaturedVehicleCampaign } from 'store/slices/buyerVehicles/thunks/featuredVehicles';
import { getListConfig } from './buyerSelectors';
import {
	hydrateVehiclesOfferRefreshV4,
	loadVehiclesMarketplace,
	setVehicleAsHidden,
	unHideVehicle,
} from './buyerVehiclesThunks';
import {
	normalizeVehicle,
	normalizeVehicleChanges,
	normalizeVehicleId,
	removeVehicleFromListKeyAndUpdateEntities,
	removeInactiveVdpEntity,
	updateVehicleIdsForAListKeyAndUpdateEntities,
	vehiclesAdapter,
} from './helpers/vehicles';
import { getVdpDetailsContext, isCurrentVdpDetailsRequest } from './helpers/vdpDetails';
import { getVdpVehicleDetails } from './vdpThunks';

const initialVdpState = {
	...initSearchList(VDP_SEARCH_LIST_KEY)[VDP_SEARCH_LIST_KEY],
	openAsModal: { isOpened: false, vehicleId: null, campaignId: null },
	detailsRequestStatus: { isLoading: false, isReady: false, errorMessage: '', requestId: null },
};

const getOfferList = (state, { searchListKey }) => state.searchLists[searchListKey];

export const initialState = vehiclesAdapter.getInitialState({
	...searchInitialState,
	searchLists: {
		...searchInitialState.searchLists,
		[VDP_SEARCH_LIST_KEY]: initialVdpState,
	},
	focussedTab: MARKETPLACE_TAB.marketplace,
	focussedTabsSincePageLoads: [MARKETPLACE_TAB.marketplace],
});

export const buyerTimedSaleVehiclesSlice = createSlice({
	name: 'buyerVehicles',
	initialState,
	reducers: {
		clearActiveVdpVehicle: (state, { payload: { vehicleId } }) => {
			const vdpList = state.searchLists[VDP_SEARCH_LIST_KEY];
			if (vdpList.vehicleIds[0] !== normalizeVehicleId(vehicleId)) return;
			Object.assign(vdpList, initialVdpState);
			removeInactiveVdpEntity(state, vehicleId);
		},
		openVdpAsDrawer: (state, { payload: { vehicleId, campaignId } }) => {
			const vdpList = state.searchLists[VDP_SEARCH_LIST_KEY];
			const numericVehicleId = normalizeVehicleId(vehicleId);
			const previousVehicleId = vdpList.vehicleIds[0];
			vdpList.vehicleIds = [numericVehicleId];
			vdpList.openAsModal = { isOpened: true, vehicleId: numericVehicleId, campaignId };
			if (previousVehicleId !== numericVehicleId) {
				vdpList.detailsRequestStatus = initialVdpState.detailsRequestStatus;
				Object.assign(vdpList, initialOfferState);
			}
			if (previousVehicleId != null && previousVehicleId !== numericVehicleId) {
				removeInactiveVdpEntity(state, previousVehicleId);
			}
		},
		closeVdpAsDrawer: (state) => {
			const vdpList = state.searchLists[VDP_SEARCH_LIST_KEY];
			const vehicleId = vdpList.vehicleIds[0];
			Object.assign(vdpList, initialVdpState);
			if (vehicleId != null) removeInactiveVdpEntity(state, vehicleId);
		},
		forceVehicleReRender: (state, { payload: { id } }) => {
			const vehicle = state.entities[id];
			if (!vehicle) return;
			const localVersion = vehicle.localVersion || 0;
			if (localVersion >= 100) return;
			vehicle.localVersion = localVersion + 1;
			if (vehicle.offerData) vehicle.offerData.localVersion = localVersion + 1;
		},
		startBid: (state, { payload }) => {
			const { id } = payload;
			const entity = state.entities[id];

			const currentAuctionStatus = entity?.bid_sale_auction_status;
			let newAuctionStatus = currentAuctionStatus;
			if (!currentAuctionStatus || currentAuctionStatus === BID_SALE_AUCTION_STATUS.pending) {
				newAuctionStatus = BID_SALE_AUCTION_STATUS.live;
			}

			vehiclesAdapter.updateOne(state, {
				id: normalizeVehicleId(id),
				changes: { bid_sale_auction_status: newAuctionStatus },
			});
		},
		moveVehiclesToLiveOnAuctionStart: (state, { payload: { bid_sale_auction_id } }) => {
			Object.keys(state.entities).forEach((id) => {
				const vehicle = state.entities[id];
				if (vehicle) {
					const currentAuctionStatus = state.entities[id].bid_sale_auction_status;
					const isVehicleOnCurrentAuction =
						bid_sale_auction_id && vehicle.bid_sale_auction_id === bid_sale_auction_id;
					if (
						isVehicleOnCurrentAuction &&
						(!currentAuctionStatus || currentAuctionStatus === BID_SALE_AUCTION_STATUS.pending)
					) {
						vehiclesAdapter.updateOne(state, {
							id: normalizeVehicleId(id),
							changes: { bid_sale_auction_status: BID_SALE_AUCTION_STATUS.live },
						});
					}
				}
			});
		},
		updateBid: (state, { payload: { id, changes: encodedChanges, loggedUser } }) => {
			const changes = updateOfferStatus({
				currentOfferState: state.entities[id],
				updatedVehicleId: id,
				updatedOfferState: encodedChanges,
				loggedUser,
			});

			if (changes) {
				vehiclesAdapter.updateOne(state, {
					id: normalizeVehicleId(id),
					changes: normalizeVehicleChanges(changes),
				});
			}
		},
		updateBids: (state, { payload: { vehicles, loggedUser } }) => {
			const updates = vehicles.reduce((accumulator, vehicle) => {
				const changes = updateOfferStatus({
					currentOfferState: state.entities[vehicle.id],
					updatedVehicleId: vehicle.id,
					updatedOfferState: vehicle,
					loggedUser,
				});

				if (changes) {
					accumulator.push({
						id: normalizeVehicleId(vehicle.id),
						changes: normalizeVehicleChanges(changes),
					});
				}
				return accumulator;
			}, []);

			if (updates.length) vehiclesAdapter.updateMany(state, updates);
		},
		markVehicleAsViewed: (state, { payload: { vehicleId } }) => {
			if (!state.entities[vehicleId]) return;

			vehiclesAdapter.updateOne(state, { id: normalizeVehicleId(vehicleId), changes: { viewed: true } });
		},
		setOvertimeCompleted: (state, { payload: { id } }) => {
			const changes = setOvertimeCompletedHelper({ currentOfferState: state.entities[id] });
			vehiclesAdapter.updateOne(state, {
				id: normalizeVehicleId(id),
				changes: normalizeVehicleChanges(changes),
			});
		},
		removeVehicleFromCurrentPage: (state, { payload }) => {
			const { vehicleId, listKey } = payload;
			removeVehicleFromListKeyAndUpdateEntities({ state, vehicleId: normalizeVehicleId(vehicleId), listKey });
		},
		addVehicleToList: (state, { payload: { vehicle, searchListKey } }) => {
			const numericVehicle = normalizeVehicle(vehicle);
			const { totalElements, itemsPerPage, vehicleIds } = state.searchLists[searchListKey];

			const isVehicleAlreadyInList = vehicleIds.includes(numericVehicle.id);

			if (!isVehicleAlreadyInList) {
				state.searchLists[searchListKey].totalElements = totalElements + 1;
				state.searchLists[searchListKey].pageCount = calculatePages(
					state.searchLists[searchListKey].totalElements,
					itemsPerPage,
				);

				if (vehicleIds.length < itemsPerPage) {
					// once tbd_126623_unified_marketplace_response we can delete next line.
					vehiclesAdapter.upsertOne(state, numericVehicle);
					vehicleIds.push(numericVehicle.id);
				}
			}
		},
		// modifying vehicle list
		// IMPORTANT CONTEXT: we know that list ir ordered by bid_sale_id desc so we can know where to add or remove elements based on that
		addToWatchlist: (state, { payload }) => {
			const { listKey } = payload;
			const vehicle = normalizeVehicle(payload.vehicle);
			const listKeyForWatchlist = listKey || MARKETPLACE_TAB.watchlist;
			const { entities } = state;
			const currentPage = state.searchLists[listKeyForWatchlist].currentSearch.params.page;
			const { vehicleIds, itemsPerPage } = state.searchLists[listKeyForWatchlist];

			// we only insert into the list if it's on first page or the list is not full
			if (state.searchLists[listKeyForWatchlist].totalElements < itemsPerPage || currentPage === 1) {
				insertVehicleIntoIdslist(entities, vehicleIds, vehicle, itemsPerPage);
			}

			// it should exist on entities, since user is clicking some card button, this is defensive code
			if (state.entities[vehicle.id]) {
				state.entities[vehicle.id].watching = true;
			} else {
				vehiclesAdapter.addOne(state, { ...vehicle, watching: true });
			}

			const { totalElements, pageCount } = calculatePaginationAfterAddition(
				state.searchLists[listKeyForWatchlist].totalElements,
				itemsPerPage,
			);
			state.searchLists[listKeyForWatchlist].totalElements = totalElements;
			state.searchLists[listKeyForWatchlist].pageCount = pageCount;
			if (state.searchLists[listKeyForWatchlist].totalElements === 1) {
				state.searchLists[listKeyForWatchlist].currentSearch.params.page = 1;
			}
		},
		removeFromWatchlist: (state, { payload }) => {
			const { vehicle, doNotModifyCurrentPage, watchlistListKey } = payload;
			const vehicleId = normalizeVehicleId(vehicle.id);
			const { entities } = state;

			// remove from list
			if (!doNotModifyCurrentPage) {
				const { vehicleIds } = removeFromVehicleIds({
					vehicleIds: state.searchLists[watchlistListKey].vehicleIds,
					vehicleIdToRemove: vehicleId,
					totalElements: state.searchLists[watchlistListKey].totalElements,
					itemsPerPage: state.searchLists[watchlistListKey].itemsPerPage,
				});
				state.searchLists[watchlistListKey].vehicleIds = vehicleIds;
			}

			state.searchLists[watchlistListKey].totalElements -= 1;
			state.searchLists[watchlistListKey].pageCount = calculatePages(
				state.searchLists[watchlistListKey].totalElements,
				state.searchLists[watchlistListKey].itemsPerPage,
			);

			if (entities[vehicleId]) state.entities[vehicleId].watching = false;
		},
		changeFollowing: (state, { payload }) => {
			const { dealershipId, isFollowing } = payload;
			Object.keys(state.entities).forEach((key) => {
				const vehicle = state.entities[key];
				if (
					vehicle &&
					Boolean(vehicle.dealership_id) &&
					String(vehicle.dealership_id) === String(dealershipId)
				) {
					vehiclesAdapter.updateOne(state, {
						id: normalizeVehicleId(key),
						changes: { following: isFollowing },
					});
				}
			});
		},
		setFocussedTab: (state, { payload }) => {
			const { focussedTab } = payload;
			state.focussedTab = focussedTab;
			if (!state.focussedTabsSincePageLoads.includes(focussedTab)) {
				state.focussedTabsSincePageLoads.push(focussedTab);
			}
		},
		// Remove when deleting tbd_187929_unified_marketplace_react mixed experience
		setUnifiedMarketplaceSaleType: (state, { payload }) => {
			const { saleType } = payload;
			state.saleType = saleType;
		},
		clearFeaturedVehicleCampaign: (state, { payload }) => {
			const { searchListKey, id } = payload;
			const featuredState = state.searchLists[searchListKey].featured;
			const remainingCampaigns = (featuredState.featuredCampaigns || []).filter(
				(campaign) => String(campaign.id) !== String(id),
			);

			featuredState.featuredCampaigns = remainingCampaigns;
		},
		// this is used only for unit testing
		clearAllVehiclesList: (state) => {
			Object.keys(state.searchLists).forEach((key) => {
				state.searchLists[key].vehicleIds = [];
			});
			vehiclesAdapter.removeAll(state);
		},
		createNewSearchList: (state, { payload }) => {
			const { searchListKey, setAsFocussed, listConfig } = payload;

			// Create new search list with the same initial structure
			const newSearchList = initSearchList(searchListKey, listConfig);

			// Add to searchLists
			state.searchLists = {
				...state.searchLists,
				...newSearchList,
			};

			// Set as focussed if requested
			if (setAsFocussed) {
				state.focussedTab = searchListKey;
				if (!state.focussedTabsSincePageLoads.includes(searchListKey)) {
					state.focussedTabsSincePageLoads.push(searchListKey);
				}
			}
		},
	},
	extraReducers: (builder) => {
		builder.addCase(getVdpVehicleDetails.pending, (state, { meta }) => {
			const { isDrawerMode } = meta.arg;
			const { vdpList, vehicleId } = getVdpDetailsContext(state, meta);
			const previousVehicleId = vdpList.vehicleIds[0];
			if (isDrawerMode && previousVehicleId !== vehicleId) return;
			if (!isDrawerMode) {
				if (previousVehicleId !== vehicleId) {
					vdpList.vehicleIds = [vehicleId];
					Object.assign(vdpList, initialOfferState);
					if (previousVehicleId != null) removeInactiveVdpEntity(state, previousVehicleId);
				}
				vdpList.openAsModal = initialVdpState.openAsModal;
			}
			vdpList.detailsRequestStatus = {
				isLoading: true,
				isReady: false,
				errorMessage: '',
				requestId: meta.requestId,
			};
		});
		builder.addCase(getVdpVehicleDetails.fulfilled, (state, { meta, payload }) => {
			const context = getVdpDetailsContext(state, meta);
			if (!isCurrentVdpDetailsRequest(context, meta.requestId) || payload.vehicle?.id !== context.vehicleId) {
				return;
			}
			const { vdpList, vehicleId } = context;

			const currentVehicle = state.entities[vehicleId] || {};
			const details = payload.vehicle;

			vehiclesAdapter.upsertOne(state, {
				...currentVehicle,
				...details,
			});
			vdpList.detailsRequestStatus = { isLoading: false, isReady: true, errorMessage: '', requestId: null };
		});
		builder.addCase(getVdpVehicleDetails.rejected, (state, { meta, payload }) => {
			const context = getVdpDetailsContext(state, meta);
			if (!isCurrentVdpDetailsRequest(context, meta.requestId)) return;
			const { vdpList } = context;
			vdpList.detailsRequestStatus = {
				isLoading: false,
				isReady: false,
				errorMessage: payload?.errorMessage || 'Unable to get vehicle details',
				requestId: null,
			};
			Object.assign(vdpList, initialOfferState);
		});
		builder.addCase(hydrateVehiclesOfferRefreshV4.pending, (state, { meta }) => {
			const list = getOfferList(state, meta.arg);
			if (!list || (meta.arg.isVdp && list.vehicleIds[0] !== normalizeVehicleId(meta.arg.vehicles[0]?.id))) {
				return;
			}
			list.offerStateLoading = true;
			list.offerStateErrorMessage = '';
			list.offerStateRequestId = meta.requestId;
		});
		builder.addCase(hydrateVehiclesOfferRefreshV4.fulfilled, (state, { meta }) => {
			const list = getOfferList(state, meta.arg);
			if (!list || list.offerStateRequestId !== meta.requestId) return;
			Object.assign(list, initialOfferState);
		});
		builder.addCase(hydrateVehiclesOfferRefreshV4.rejected, (state, { meta, payload }) => {
			const list = getOfferList(state, meta.arg);
			if (!list || list.offerStateRequestId !== meta.requestId) return;
			if (meta.arg.isVdp && list.vehicleIds[0] !== normalizeVehicleId(meta.arg.vehicles[0]?.id)) return;
			list.offerStateLoading = false;
			list.offerStateErrorMessage =
				meta.aborted || payload?.cancelled ? '' : payload?.errorMessage || 'something went wrong';
			list.offerStateRequestId = null;
		});
		builder.addCase(HYDRATE, (state, action) => {
			// just merge the data on hydrate
			return {
				...state,
				focussedTab: action.payload.buyerVehicles?.focussedTab,
				saleType: action.payload.buyerVehicles?.saleType,
			};
		});
		// Hide vehicle
		builder.addCase(setVehicleAsHidden.pending, (state, { meta }) => {
			state.entities[meta.arg.vehicle.id].hideVehicleRequestIsLoading = true;
		});
		builder.addCase(setVehicleAsHidden.fulfilled, (state, { meta }) => {
			const hiddenVehicle = state.entities[meta.arg.vehicle.id];
			const { searchListKey } = meta.arg;
			const { removeOnHide } = getListConfig(state, searchListKey);

			// set vehicle as hidden
			hiddenVehicle.hideVehicleRequestIsLoading = false;
			hiddenVehicle.hidden = true;
			hiddenVehicle.watching = false;
			const hiddenSearchList = state.searchLists[MARKETPLACE_TAB.hidden];

			// insert into hidden list
			insertVehicleIntoIdslist(
				state.entities,
				hiddenSearchList.vehicleIds,
				hiddenVehicle,
				hiddenSearchList.itemsPerPage,
			);
			const { totalElements: totalElementsAfterAddition, pageCount: pageCountAfterAddition } =
				calculatePaginationAfterAddition(hiddenSearchList.totalElements, hiddenSearchList.itemsPerPage);
			hiddenSearchList.totalElements = totalElementsAfterAddition;
			hiddenSearchList.pageCount = pageCountAfterAddition;

			//this could happens when using it from FeaturedVehicleCampaign or Vdp that doesn't have searchListKey
			if (searchListKey && removeOnHide) {
				// remove from the list where it was hidden
				removeVehicleFromListKeyAndUpdateEntities({
					state,
					vehicleId: meta.arg.vehicle.id,
					listKey: searchListKey,
				});
			}
			toast.success('Vehicle successfully hidden');
		});
		builder.addCase(setVehicleAsHidden.rejected, (state, { meta }) => {
			toast.error('Hide action has failed, please retry');
			if (state.entities[meta.arg.vehicle.id]) {
				state.entities[meta.arg.vehicle.id].hideVehicleRequestIsLoading = false;
			}
		});

		// UnHide vehicle
		builder.addCase(unHideVehicle.pending, (state, { meta }) => {
			if (state.entities[meta.arg.vehicleToUnHide.id]) {
				state.entities[meta.arg.vehicleToUnHide.id].hideVehicleRequestIsLoading = true;
			}
		});
		builder.addCase(unHideVehicle.fulfilled, (state, { meta }) => {
			const vehicleIdToRemove = meta.arg.vehicleToUnHide.id;
			const hiddenVehicle = state.entities[vehicleIdToRemove];

			if (!hiddenVehicle) return;
			const hiddenListKey = MARKETPLACE_TAB.hidden;
			hiddenVehicle.hideVehicleRequestIsLoading = false;
			hiddenVehicle.hidden = false;

			removeVehicleFromListKeyAndUpdateEntities({ state, vehicleId: vehicleIdToRemove, listKey: hiddenListKey });
			toast.success('Vehicle successfully unhidden');
		});
		builder.addCase(unHideVehicle.rejected, (state, { meta }) => {
			toast.error('Unhide has failed, please retry');
			if (state.entities[meta.arg.vehicleToUnHide.id]) {
				state.entities[meta.arg.vehicleToUnHide.id].hideVehicleRequestIsLoading = false;
			}
		});
		builder.addCase(loadVehiclesMarketplace.pending, (state, { meta }) => {
			const list = state.searchLists[meta.arg.searchListKey];
			list.isLoading = true;
			list.errorMessage = '';
			Object.assign(list, initialOfferState);
		});
		builder.addCase(loadVehiclesMarketplace.fulfilled, (state, { payload, meta }) => {
			const { vehicles, pageCount, totalCount, page } = payload;
			const listKey = meta.arg.searchListKey;
			updateVehicleIdsForAListKeyAndUpdateEntities({
				newVehicleList: vehicles,
				state,
				listKey,
				shouldDecode: !!meta?.arg?.unifiedMarketplace,
			});
			state.searchLists[listKey].isLoading = false;
			state.searchLists[listKey].isReady = true;
			state.searchLists[listKey].pageCount = pageCount;
			state.searchLists[listKey].totalElements = totalCount;
			state.searchLists[listKey].currentSearch.params.page = page;
		});
		builder.addCase(loadVehiclesMarketplace.rejected, (state, { meta, payload }) => {
			if (!meta.aborted) {
				const listKey = meta.arg.searchListKey;
				// clear list
				updateVehicleIdsForAListKeyAndUpdateEntities({
					newVehicleList: [],
					state,
					listKey,
					shouldDecode: !!meta?.arg?.unifiedMarketplace,
				});
				state.searchLists[listKey].isLoading = false;
				state.searchLists[listKey].isReady = true;
				state.searchLists[listKey].errorMessage = payload?.errorMessage;
			}
		});
		// Featured
		builder.addCase(loadFeaturedVehicleCampaign.pending, (state, { meta }) => {
			state.searchLists[meta.arg.searchListKey].featured.isLoading = true;
			state.searchLists[meta.arg.searchListKey].featured.errorMessage = '';
		});
		builder.addCase(loadFeaturedVehicleCampaign.fulfilled, (state, { payload, meta }) => {
			const { featuredCampaigns } = payload;
			const listKey = meta.arg.searchListKey;
			state.searchLists[listKey].featured.isLoading = false;
			state.searchLists[listKey].featured.featuredCampaigns = featuredCampaigns;

			featuredCampaigns?.forEach((fc) =>
				updateVehicleIdsForAListKeyAndUpdateEntities({
					state,
					newVehicleList: fc.vehicles,
					listKey,
					isFeaturedVehicleList: true,
					campaignType: fc.campaignType,
				}),
			);
		});
		builder.addCase(loadFeaturedVehicleCampaign.rejected, (state, { meta, error }) => {
			state.searchLists[meta.arg.searchListKey].featured.isLoading = false;
			state.searchLists[meta.arg.searchListKey].featured.errorMessage =
				error?.message || 'Error getting featured vehicles';
		});
		builder.addCase(closeCampaign.fulfilled, (state, { meta }) => {
			const listKey = meta.arg.searchListKey;
			const featuredState = state.searchLists[listKey].featured;
			const remainingCampaigns = (featuredState.featuredCampaigns || []).filter(
				(campaign) => String(campaign.id) !== String(meta.arg.id),
			);

			featuredState.featuredCampaigns = remainingCampaigns;
		});
	},
});

export const buyerTimedSaleVehiclesSliceActions = buyerTimedSaleVehiclesSlice.actions;
export const {
	/* Export ///NEW ACTIONS */
	clearActiveVdpVehicle,
	openVdpAsDrawer,
	closeVdpAsDrawer,
	forceVehicleReRender,
	startBid,
	updateBid,
	updateBids,
	markVehicleAsViewed,
	setOvertimeCompleted,
	removeVehicleFromCurrentPage,
	addVehicleToList,
	addToWatchlist,
	removeFromWatchlist,
	changeFollowing,
	setFocussedTab,
	clearFeaturedVehicleCampaign,
	clearAllVehiclesList,
	moveVehiclesToLiveOnAuctionStart,
	setUnifiedMarketplaceSaleType,
	createNewSearchList,
} = buyerTimedSaleVehiclesSlice.actions;

export const {
	selectById: selectVehicleById,
	selectIds: selectVehicleIds,
	selectEntities: selectVehicleEntities,
	selectAll: selectAllVehicles,
	selectTotal: selectTotalVehicles,
} = vehiclesAdapter.getSelectors((state) => state.buyerVehicles);

export default buyerTimedSaleVehiclesSlice.reducer;
